import React, { useMemo } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, GlobalStyles } from '@mui/material';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { getRoutes } from 'utils/auth';

import { sliceKey, reducer } from './redux/headerSlice/slice';
import { headerSaga } from './redux/headerSlice/saga';
import { selectDealerSelected } from './redux/headerSlice/selectors';
import AppProvider from './globalContexts';
import { Layout } from './features/layout/Layout';
import { HomePage } from './features/home/HomePage';
import { TargetPage } from './features/TargetPage/TargetPage';
import MeasurePage from './features/MeasurePage';
import { AskMiaMeasurePage } from './features/MeasurePage/AskMiaMeasurePageLoadable';
import { GeneratePage } from './features/GeneratePage/GeneratePage';
import { AnalyzePage } from './features/AnalyzePage/AnalyzePage';
import ConfigurePage from './features/ConfigurePage/ConfigurePage';
import { SettingsPage } from './features/SettingsPage/SettingsPage';
import { AdminPage } from './features/AdminPage/AdminPage';
import ContentPage from './features/ContentPage/ContentPage';
import { NotFoundPage } from './features/NotFoundPage';
import DealerSelectionFallback from './sharedComponents/DealerSelectionFallback';
import { routesMap } from 'app/components/AuthRouter/authRoutes';

// Route to component mapping for NewAIAvatarLayout
const aiAvatarRouteMap: Record<string, React.ComponentType<any>> = {
  '/ai-avatar-layout': HomePage,
  '/ai-avatar-layout/measure': MeasurePage,
  '/ai-avatar-layout/measure/ask-mia': AskMiaMeasurePage,
  '/ai-avatar-layout/target': TargetPage,
  '/ai-avatar-layout/generate': GeneratePage,
  '/ai-avatar-layout/analyze': AnalyzePage,
  '/ai-avatar-layout/configure': ConfigurePage,
  '/ai-avatar-layout/content': ContentPage,
  '/ai-avatar-layout/settings': SettingsPage,
  '/ai-avatar-layout/admin': AdminPage,
};

// Component name to route mapping (for resource_mappings)
const componentToRouteMap: Record<string, string> = {
  'AIAvatarHomePage': '/ai-avatar-layout',
  'AIAvatarMeasurePage': '/ai-avatar-layout/measure',
  'AIAvatarTargetPage': '/ai-avatar-layout/target',
  'AIAvatarGeneratePage': '/ai-avatar-layout/generate',
  'AIAvatarAnalyzePage': '/ai-avatar-layout/analyze',
  'AIAvatarConfigurePage': '/ai-avatar-layout/configure',
  'AIAvatarContentPage': '/ai-avatar-layout/content',
  'AIAvatarSettingsPage': '/ai-avatar-layout/settings',
  'AIAvatarAdminPage': '/ai-avatar-layout/admin',
};


export const AIAvatarLayout: React.FC = () => {
  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  const selectedDealer = useSelector(selectDealerSelected);
  
  // Check if dealer is selected
  const isDealerSelected = selectedDealer && 
    selectedDealer !== 'default' && 
    selectedDealer.dealer && 
    selectedDealer.dealer.trim() !== '';

  // Get routes from resource_mappings
  // Use state to allow re-rendering when routes change
  const [resourceMappings, setResourceMappings] = React.useState<any[]>(() => {
    try {
      return getRoutes() || [];
    } catch (error) {
      console.error('Error getting routes:', error);
      return [];
    }
  });

  // Update routes when localStorage changes
  React.useEffect(() => {
    const updateRoutes = () => {
      try {
        const routes = getRoutes() || [];
        setResourceMappings(routes);
      } catch (error) {
        console.error('Error updating routes:', error);
      }
    };

    // Initial load
    updateRoutes();

    // Listen for storage changes (when user_token is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user_token') {
        updateRoutes();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get allowed routes for NewAIAvatarLayout
  const allowedRoutes = useMemo(() => {
    const allowed: Array<{ path: string; component: React.ComponentType<any> }> = [];
    const seenPaths = new Set<string>();
    
    // Filter routes that belong to NewAIAvatarLayout
    resourceMappings.forEach((route: any) => {
      const routePath = route.path || '';
      const componentName = route.component || '';
      
      // Check if route is a NewAIAvatarLayout route by path
      if (routePath.startsWith('/ai-avatar-layout')) {
        if (!seenPaths.has(routePath)) {
          const component = aiAvatarRouteMap[routePath];
          if (component) {
            allowed.push({ path: routePath, component });
            seenPaths.add(routePath);
          }
        }
      }
      
      // Also check by component name if path doesn't match but component is NewAIAvatarLayout component
      if (componentName.startsWith('AIAvatar')) {
        const mappedPath = componentToRouteMap[componentName];
        if (mappedPath) {
          const finalPath = routePath || mappedPath;
          if (!seenPaths.has(finalPath)) {
            const component = routesMap[componentName] || aiAvatarRouteMap[mappedPath];
            if (component) {
              allowed.push({ path: finalPath, component });
              seenPaths.add(finalPath);
            }
          }
        }
      }
    });

    return allowed;
  }, [resourceMappings]);

  return (
    <>
      <GlobalStyles
        styles={(theme) => ({
          // Global scrollbar styles for theme compatibility
          '*': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[500],
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
            },
          },
        })}
      />
      <AppProvider>
        <Layout>
          {!isDealerSelected ? (
            // Show dealer selection fallback page if no dealer is selected
            <DealerSelectionFallback />
          ) : (
            // Show actual pages only when dealer is selected
            // Routes are now loaded from resource_mappings - only show routes that are in resource_mappings
            <Switch>
              {/* Add Ask Mia Measure Page route - sub-route of measure page */}
              <Route exact path="/ai-avatar-layout/measure/ask-mia" component={AskMiaMeasurePage} />
              {allowedRoutes.length > 0 ? (
                allowedRoutes.map((route, index) => (
                  <Route
                    key={`${route.path}-${index}`}
                    exact
                    path={route.path}
                    component={route.component}
                  />
                ))
              ) : null}
              {/* Show 404 page for any unmatched routes or if no routes available */}
              <Route component={NotFoundPage} />
            </Switch>
          )}
        </Layout>
      </AppProvider>
    </>
  );
};


